import { kebabToCamel, tryParseDataJSON } from "./utils";

export class AttributeHandler {
  private readonly hasValueProp: boolean;

  constructor(
    private readonly element: HTMLElement & { [key: string]: any },
    private readonly attributes: string[],
    private readonly hasModelValueProp: boolean,
  ) {
    this.hasValueProp = attributes.includes("value");
  }

  parseDatasetAttributes(): void {
    // Prefer dataset, but fallback to getAttribute for Chrome timing issues during early lifecycle
    const ds = this.element.dataset; // NOSONAR
    if (!ds || Object.keys(ds).length === 0) return;

    try {
      const propsSet = new Set(this.attributes);
      for (const [key, raw] of Object.entries(ds)) {
        if (!propsSet.has(key)) continue;
        const parsed = tryParseDataJSON(raw);
        if (parsed !== undefined) {
          this.element[key] = parsed;
        }
      }
    } catch (error) {
      console.debug("data-* pre-parse failed", error);
    }
  }

  normalizeModelValue(): void {
    // Skip normalization if component has its own 'value' prop
    if (this.hasValueProp) return;

    const mvProp = this.element.modelValue;
    const mvAttr = this.element.getAttribute("model-value");
    const vAttr = this.element.getAttribute("value");

    if (mvProp == null) {
      if (mvAttr != null) {
        return;
      } else if (vAttr != null) {
        this.element.setAttribute("model-value", vAttr);
        this.element.removeAttribute("value");
      }
    } else if (vAttr != null) {
      this.element.removeAttribute("value");
    }
  }

  handleDataAttributeChange(name: string, newValue?: string): boolean {
    if (!name.startsWith("data-")) return false;
    const propName = kebabToCamel(name.slice(5));
    if (!this.attributes.includes(propName)) return false;
    const parsed = typeof newValue === "string" ? tryParseDataJSON(newValue) : undefined;
    if (parsed !== undefined) {
      this.element[propName] = parsed;
      return true;
    }
    return false;
  }

  handleModelValueChange(name: string, newValue?: string): boolean {
    if (!this.hasModelValueProp) return false;

    if (name === "model-value") {
      this.element.modelValue = newValue;
      return true;
    }

    // Only intercept 'value' for v-model if component doesn't have its own 'value' prop
    // (e.g., Checkbox has both modelValue and value props - don't intercept)
    if (name === "value" && !this.hasValueProp) {
      this.#handleValueAttributeChange(newValue);
      return true;
    }
    return false;
  }

  readonly #handleValueAttributeChange = (newValue?: string): void => {
    if (newValue == null) {
      queueMicrotask(() => this.element.removeAttribute("value"));
      return;
    }

    if (!this.element.isConnected) {
      this.#handleValueBeforeConnect(newValue);
      return;
    }

    this.#handleValueAfterConnect(newValue);
  };

  readonly #handleValueBeforeConnect = (newValue: string): void => {
    const hasPreset = this.element.modelValue != null;

    if (hasPreset) {
      queueMicrotask(() => this.element.removeAttribute("value"));
      return;
    }

    if (this.element.getAttribute("model-value") !== newValue) {
      this.element.setAttribute("model-value", newValue);
    }
    queueMicrotask(() => this.element.removeAttribute("value"));
  };

  readonly #handleValueAfterConnect = (newValue: string): void => {
    this.element.modelValue = newValue;
    queueMicrotask(() => this.element.removeAttribute("value"));
  };
}
